import type { ResolvedDesignAreaConfig } from '@pz/scene';

/**
 * The phone sub-panels' arithmetic: what a stepper shows, and what the model
 * stores.
 *
 * Every mapping the shopper sees on a phone is here and nowhere else, without
 * a line of DOM, because this is the part that is actually worth testing: the
 * widget's test runner has no DOM, so a conversion written inside a click
 * handler is a conversion nobody will ever check again. The model units are
 * the ones in `design-area.ts` (millimetres, 0–1 opacity, per-mille tracking,
 * −100…100 bend); the shown units are Zazzle's (percent, em, thickness 1–200).
 */

export interface RangeSpec {
  min: number;
  max: number;
  /** What the − / + buttons and the arrow keys move by. */
  step: number;
  /** Finer movement for the slider under the stepper; defaults to `step`. */
  sliderStep?: number;
  /** Digits after the decimal point in the field. */
  decimals: number;
  /** Shown after the number ("mm", "%", "°"); empty for a bare number. */
  unit?: string;
}

/** Halves the rounding error of `toFixed` at the sizes we deal with. */
export function roundTo(value: number, decimals: number): number {
  const factor = 10 ** Math.max(0, Math.min(6, Math.round(decimals)));
  return Math.round(value * factor) / factor;
}

/**
 * A value inside its range, on the step grid, rounded to the range's
 * precision.
 *
 * The grid is anchored at ZERO, not at `min`: Zazzle's stroke thickness runs
 * 1…200 and its default is 75, and a grid anchored at the minimum would turn
 * that 75 into 76 the moment anything re-read it. Multiples of the step with
 * the ends clamped keeps round numbers round and still honours the bounds.
 */
export function clampToStep(value: number, range: RangeSpec): number {
  if (!Number.isFinite(value)) return range.min;
  const bounded = Math.min(range.max, Math.max(range.min, value));
  if (!(range.step > 0)) return roundTo(bounded, range.decimals);
  const snapped = Math.round(bounded / range.step) * range.step;
  return roundTo(Math.min(range.max, Math.max(range.min, snapped)), range.decimals);
}

/** One press of − or +, clamped and snapped like everything else. */
export function stepValue(value: number, range: RangeSpec, direction: -1 | 1): number {
  return clampToStep(clampToStep(value, range) + direction * range.step, range);
}

/** Serbian writes a decimal comma; the field accepts either. */
export function formatValue(value: number, decimals: number): string {
  const text = roundTo(value, decimals).toFixed(Math.max(0, decimals));
  return decimals > 0 ? text.replace('.', ',') : text;
}

/** Reads a typed field; `undefined` when it is not a number at all. */
export function parseValue(text: string): number | undefined {
  const cleaned = text.replace(',', '.').replace(/[^\d.\-]/g, '').trim();
  if (!cleaned || cleaned === '-' || cleaned === '.') return undefined;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : undefined;
}

// ── Fixed ranges (the table in spec §3) ─────────────────────────────────────

/** Opacity: the model keeps 0–1, the shopper sees 1–100 %. */
export const OPACITY_RANGE: RangeSpec = { min: 1, max: 100, step: 1, decimals: 0, unit: '%' };
/** Letter spacing: Zazzle's −1…3 em, narrowed to what our renderer honours. */
export const TRACKING_RANGE: RangeSpec = { min: -0.5, max: 1, step: 0.05, sliderStep: 0.01, decimals: 2, unit: 'em' };
/** Line height, a multiple of the font size. */
export const LINE_SPACING_RANGE: RangeSpec = { min: 0.5, max: 3, step: 0.05, decimals: 2 };
/** Curved text: −1 (arc down) … 1 (arc up); the model stores −100…100. */
export const BEND_RANGE: RangeSpec = { min: -1, max: 1, step: 0.05, sliderStep: 0.01, decimals: 2 };
/** Rotation in whole degrees; the buttons move by 15° like the desktop panel. */
export const ROTATION_RANGE: RangeSpec = { min: 0, max: 359, step: 15, sliderStep: 1, decimals: 0, unit: '°' };
/** Text stroke thickness: Zazzle's 1–200, which is `outline.width` × 400. */
export const STROKE_RANGE: RangeSpec = { min: 1, max: 200, step: 5, sliderStep: 1, decimals: 0 };
/** Shadow direction, offset and blur, exactly Zazzle's ranges. */
export const SHADOW_ANGLE_RANGE: RangeSpec = { min: -180, max: 180, step: 5, sliderStep: 1, decimals: 0, unit: '°' };
export const SHADOW_OFFSET_RANGE: RangeSpec = { min: 1, max: 100, step: 1, decimals: 0, unit: '%' };
export const SHADOW_BLUR_RANGE: RangeSpec = { min: 0, max: 100, step: 1, decimals: 0, unit: '%' };
/** Pattern gaps, in millimetres of the print zone. */
export const PATTERN_GAP_RANGE: RangeSpec = { min: 0, max: 200, step: 1, sliderStep: 0.5, decimals: 1, unit: 'mm' };
/** A shape's line weight. */
export const SHAPE_STROKE_RANGE: RangeSpec = { min: 0, max: 20, step: 0.1, decimals: 1, unit: 'mm' };
/** Crop zoom (model 1–8) as a percentage, and the straighten angle. */
export const CROP_ZOOM_RANGE: RangeSpec = { min: 100, max: 800, step: 10, sliderStep: 1, decimals: 0, unit: '%' };
export const CROP_STRAIGHTEN_RANGE: RangeSpec = { min: -45, max: 45, step: 1, decimals: 0, unit: '°' };
/** Brightness and contrast, both −100…100 %. */
export const FILTER_AMOUNT_RANGE: RangeSpec = { min: -100, max: 100, step: 5, sliderStep: 1, decimals: 0, unit: '%' };

/** Font size: millimetres, bounded by what the zone allows. */
export function sizeRange(config: ResolvedDesignAreaConfig): RangeSpec {
  const max = Math.max(config.text.minSizeMm + 0.5, config.text.maxSizeMm);
  return { min: config.text.minSizeMm, max, step: 0.5, sliderStep: 0.1, decimals: 1, unit: 'mm' };
}

/**
 * The wrap width of a fixed text box.
 *
 * Twice the zone is deliberate rather than generous: a column wider than the
 * zone is how a shopper says "do not wrap this at all", and a rotated or
 * deliberately overhanging line needs the room. `current` keeps a design that
 * arrives wider than the range from being clamped the moment it is opened.
 */
export function wrapWidthRange(config: ResolvedDesignAreaConfig, currentMm = 0, minMm = 4): RangeSpec {
  const max = Math.max(config.sizeMm.w * 2, currentMm, minMm + 1);
  return { min: roundTo(minMm, 1), max: roundTo(max, 1), step: 0.5, sliderStep: 0.1, decimals: 1, unit: 'mm' };
}

// ── Model ↔ shown ───────────────────────────────────────────────────────────

export function opacityToUi(opacity: number | undefined): number {
  return clampToStep(Math.round((opacity ?? 1) * 100), OPACITY_RANGE);
}

export function uiToOpacity(value: number): number {
  return roundTo(clampToStep(value, OPACITY_RANGE) / 100, 3);
}

export function trackingToUi(tracking: number | undefined): number {
  return clampToStep((tracking ?? 0) / 1000, TRACKING_RANGE);
}

export function uiToTracking(value: number): number {
  return Math.round(clampToStep(value, TRACKING_RANGE) * 1000);
}

export function bendToUi(bend: number | undefined): number {
  return clampToStep((bend ?? 0) / 100, BEND_RANGE);
}

export function uiToBend(value: number): number {
  return Math.round(clampToStep(value, BEND_RANGE) * 100);
}

export function strokeToUi(width: number | undefined): number {
  return clampToStep(Math.round((width ?? 0) * 400), STROKE_RANGE);
}

export function uiToStroke(value: number): number {
  return roundTo(clampToStep(value, STROKE_RANGE) / 400, 4);
}

/** Rotation is stored 0–360 clockwise; the UI never shows 360. */
export function rotationToUi(rotation: number | undefined): number {
  const wrapped = (((rotation ?? 0) % 360) + 360) % 360;
  return Math.round(wrapped) % 360;
}

export function uiToRotation(value: number): number {
  const wrapped = ((Math.round(value) % 360) + 360) % 360;
  return wrapped;
}

export function cropZoomToUi(zoom: number | undefined): number {
  return clampToStep(Math.round((zoom ?? 1) * 100), CROP_ZOOM_RANGE);
}

export function uiToCropZoom(value: number): number {
  return roundTo(clampToStep(value, CROP_ZOOM_RANGE) / 100, 3);
}

export function filterAmountToUi(amount: number | undefined): number {
  return clampToStep(Math.round((amount ?? 0) * 100), FILTER_AMOUNT_RANGE);
}

export function uiToFilterAmount(value: number): number {
  return roundTo(clampToStep(value, FILTER_AMOUNT_RANGE) / 100, 2);
}
